<template>
    <div class="digital-classrooms-signup">
        <div class="digital-classrooms-signup__banner" :class="getSignUpInfo.class">
            <h1 class="digital-classrooms-signup__banner-title" :class="{ processing: isProcessingSignUp }" v-html="getSignUpInfo.title"></h1>
            <p class="digital-classrooms-signup__banner-info" v-html="getSignUpInfo.description"></p>
            <TotemButton
                v-if="!isLoggedIn"
                @click="$router.push({ 'name': 'login' })"
                color="primary"
                :width="180"
                class="digital-classrooms-signup__banner-login"
                :label="$t('Menu.login')"
            />
            <div v-if="errorCode && getNonGeneralErrorMessage" class="digital-classrooms-signup__errors" :class="{ no_access_but_signed_up: userHasNoAccessButHasSignup }">
                <span class="digital-classrooms-signup__error-icon icon-notification"></span>
                <div class="digital-classrooms-signup__error-text">
                    <span class="digital-classrooms-signup__error-notification" v-html="getNonGeneralErrorMessage.mainText"></span>
                    <span class="digital-classrooms-signup__error-message" v-html="getNonGeneralErrorMessage.subText"></span>
                </div>
            </div>
        </div>
        <template v-if="!isLoadingEvents && filterPastEvents.length > 0">
            <DigitalClassRoomTable
                    :hasAccessToSignUp="hasAccessToSignUp"
                    :userHasNoAccessButHasSignup="userHasNoAccessButHasSignup"
                >
                <DigitalClassRoomEvent
                    v-for="(event, index) in filterPastEvents"
                    :key="index"
                    :event="event"
                    :course="course"
                    :showEventName="false"
                >
                </DigitalClassRoomEvent>
            </DigitalClassRoomTable>
        </template>
        <template v-if="!isLoadingEvents && getPastSignUpsForThisEvent.length > 0">
            <h1 class="digital-classrooms-signup__banner-title-past-events" v-html="$t('DigitalClassrooms.past_signups_title')"></h1>
            <p class="digital-classrooms-signup__past-events-description" v-html="$t('DigitalClassrooms.past_signups_description')"></p>
            <DigitalClassRoomTable
                :hasAccessToSignUp="hasAccessToSignUp"
                :userHasNoAccessButHasSignup="userHasNoAccessButHasSignup"
                :pastEventsTable="true"
            >
                <DigitalClassRoomEvent
                    v-for="(event, index) in getPastSignUpsForThisEvent"
                    :key="index"
                    :event="event.training"
                    :course="course"
                    :isPastEvent="true"
                    @showPopupMessage="showPopupMessage"
                    :showEventName="false"
                >
                </DigitalClassRoomEvent>
            </DigitalClassRoomTable>
        </template>
        <div v-else-if="isLoadingEvents" class="digital-classrooms-signup__loader">
            <span class="spinner icon-spinner digital-classrooms-signup__spinner"></span>
        </div>
        <TotemToast ref="toast"/>
    </div>
</template>

<script>
    import DigitalClassRoomEvent from 'components/user/signups/DigitalClassRoomEvent'
    import DigitalClassRoomTable from 'components/coursepage/courseaddons/digitalclassrooms/DigitalClassRoomTable'

    import { mapGetters } from 'vuex'
    import moment from 'moment'

    export default {
        name: 'DigitalClassRoomSignUp',
        components: {
            DigitalClassRoomEvent,
            DigitalClassRoomTable
        },
        props: {
            course: Object
        },
        data () {
            return {
                isProcessingSignUp: false,
                isLoadingEvents: false,
                errorCode: null
            }
        },
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn',
                events: 'Courses/getEventsForSingleCourse',
                signedUpEvents: 'Courses/getSignedUpEventsForUser'
            }),
            getSignUpInfo () {
                let signUpInfo = {
                    title: this.$t('DigitalClassrooms.banner_not_signed'),
                    description: this.$t('DigitalClassrooms.banner_not_signed_info'),
                    class: ''
                }
                if (this.isKLTorPHTPackage) {
                    signUpInfo.description = this.$t('DigitalClassrooms.banner_not_signed_info_for_KLT_or_PHT_packages')
                }
                if (this.isCloudskillsOrCloudProPackage) {
                    signUpInfo.description = this.$t('DigitalClassrooms.banner_not_signed_info_for_cloudskills_or_cloudpro_packages')
                }
                if (!this.isLoggedIn) {
                    signUpInfo.title = this.$t('DigitalClassrooms.banner_log_in')
                    signUpInfo.description = this.$t('DigitalClassrooms.banner_log_in_info')
                    signUpInfo.class = 'digital-classrooms-signup__not-logged'
                    return signUpInfo
                }
                if (this.course.demo) {
                    signUpInfo.title = this.$t('DigitalClassrooms.banner_demo')
                    signUpInfo.description = this.$t('DigitalClassrooms.banner_demo_info')
                    signUpInfo.class = 'digital-classrooms-signup__demo'
                    return signUpInfo
                }
                if (this.isProcessingSignUp) {
                    signUpInfo.title = this.$t('DigitalClassrooms.banner_processing')
                    signUpInfo.description = this.$t('DigitalClassrooms.banner_processing_info')
                    signUpInfo.class = 'digital-classrooms-signup__processing'
                    return signUpInfo
                }
                if (this.isUserSignedToEventsWithCourseId || this.userHasNoAccessButHasSignup) {
                    signUpInfo.title = this.$t('DigitalClassrooms.banner_signed_up')
                    signUpInfo.description = this.$t('DigitalClassrooms.banner_signed_up_info')
                    signUpInfo.class = 'digital-classrooms-signup__signed-up'
                    return signUpInfo
                }
                if (
                    (this.noFoundTrainingsOrIdNotDefinedError && !this.isLoadingEvents) ||
                    (!this.isLoadingEvents && this.filterPastEvents.length === 0)
                ) {
                    signUpInfo.title = this.$t('DigitalClassrooms.banner_not_available')
                    signUpInfo.description = this.$t('DigitalClassrooms.banner_not_available_info')
                    signUpInfo.class = 'digital-classrooms-signup__not-logged'
                }
                return signUpInfo
            },
            isCloudskillsOrCloudProPackage () {
                const packagesToCheck = ['fi-cloudskills', 'fi-cloudpro']
                return this.course.availableInPackages.some(coursePackage => packagesToCheck.includes(coursePackage.id))
            },
            isKLTorPHTPackage () {
                return this.course.availableInPackages.find(pack => pack.id === 'fi-klt-valmennus' || pack.id === 'fi-pht-valmennus')
            },
            hasAccessToSignUp () {
                return this.isLoggedIn && !this.course.demo
            },
            isUserSignedToEventsWithCourseId () {
                return this.signedUpEvents.filter(event => this.course.id === event.training.course.id && event.subscriptionStatus === 'ok' &&
                    moment(event.training.sessions[0]?.endDateAndTime).unix() > moment().unix()).length > 0
            },
            filterPastEvents () {
                return this.events.filter(event => {
                    return moment(event.sessions[event.sessions.length - 1]?.endDateAndTime).unix() > moment().unix() &&
                        event.trainingStatus !== 'cancelled' && event.trainingStatus !== 'scheduled'
                })

                    .sort(function (a, b) {
                        return new Date(a.firstTrainingDateAndTime) - new Date(b.firstTrainingDateAndTime)
                    })
            },
            getNonGeneralErrorMessage () {
                if (!this.errorCode) {
                    return ''
                }
                if (this.notSeatsAvailableError) {
                    this.loadDigitalClassRoomSignups()
                    return {
                        mainText: this.$t('DigitalClassrooms.error_no_seats_available_main'),
                        subText: this.$t('DigitalClassrooms.error_no_seats_available_sub')
                    }
                }
                if (this.digitalClassRoomSomethingWentWrongError) {
                    return {
                        mainText: this.$t('DigitalClassrooms.error_something_went_wrong_main'),
                        subText: this.$t('DigitalClassrooms.error_something_went_wrong_sub')
                    }
                }
                if (this.errorCode === 'noAccess') {
                    return {
                        mainText: this.$t('DigitalClassrooms.error_no_access_but_signed_main'),
                        subText: this.$t('DigitalClassrooms.error_no_access_but_signed_main_sub')
                    }
                }
                return ''
            },
            noFoundTrainingsOrIdNotDefinedError () {
                return this.errorCode === 405 || this.errorCode === 400
            },
            notSeatsAvailableError () {
                return this.errorCode === 402
            },
            digitalClassRoomSomethingWentWrongError () {
                return this.errorCode === 407
            },
            userHasNoAccessButHasSignup () {
                return this.isLoggedIn && this.course.demo && this.isUserSignedToEventsWithCourseId
            },
            getPastSignUpsForThisEvent () {
                return this.signedUpEvents.filter(event => {
                    const sessions = event.training.sessions

                    return this.course.id === event.training.course.id &&
                        moment().unix() > moment(sessions[sessions.length - 1].endDateAndTime).unix()
                })
            }
        },
        methods: {
            setProcessingSignUpState (isProcessingSignUp) {
                this.isProcessingSignUp = isProcessingSignUp
            },
            showPopupMessage (popUpMessage) {
                this.$refs.toast.show(popUpMessage)
            },
            showErrorMessage (errorCode) {
                this.errorCode = errorCode
            },
            async loadDigitalClassRoomSignups () {
                try {
                    this.isLoadingEvents = true
                    await this.$store.dispatch('Courses/loadDigitalClassRoomSignUpsForCourse', this.course.id)
                } catch (error) {
                    this.errorCode = error.code
                } finally {
                    if (this.userHasNoAccessButHasSignup) {
                        this.errorCode = 'noAccess'
                    }
                    this.isLoadingEvents = false
                }
            }
        },
        created () {
            this.loadDigitalClassRoomSignups()
        }
    }
</script>

<style lang="scss" scoped>
    @include digital-classrooms-table;

    .digital-classrooms-signup {
        background: $white;
        margin: 20px 0;
        padding: 30px;
        border-radius: 20px;

        &__title {
            font-family: PPAgrandir-Medium, sans-serif;
            font-size: 3.2rem;
            margin: 0 0 35px;
        }

        &__banner {
            background: $white;
            border-radius: 10px 10px 0 0;
        }

        &__banner-title {
            font-size: 2.2rem;
        }

        &__banner-title-past-events {
            font-size: 2.4rem;
            margin: 30px 0 0 0;
        }

        &__past-events-description {
            font-size: 1.6rem;
            margin: 20px 0;
        }

        &__banner-spinner {
            font-size: 2rem;
            color: $orange;
        }

        &__banner-info {
            font-family: PlusJakartaSans-Medium, sans-serif;
            font-size: 1.6rem;
            line-height: 150%;
            margin: 20px 0;
        }

        &__table {
            display: flex;
            flex-direction: column;
            border: solid 1px $alto;
            overflow: hidden;
            border-radius: 10px;
        }

        &__headers {
            background: $gallery !important;
            font-family: Nunito-Bold !important;
            font-size: 1.4rem;
            font-weight: 700;
        }

        &__not-logged, &__demo {
            background: $peach;
            color: $black;
            border-radius: 10px;
            padding: 20px 20px 20px 60px;

            &:before {
                content: '\e92b';
                font-family: icomoon;
                font-size: 2.4rem;
                position: absolute;
                margin: 0 0 0 -40px;
            }

            .digital-classrooms-signup__banner-info {
                font-family: 'PlusJakartaSans-Medium', sans-serif;
                font-size: 1.8rem;
                margin: 20px 0 10px;
            }
        }

        &__banner-login {
            margin: 20px 0 0 0;
        }

        &__errors {
            &.no_access_but_signed_up {
                background: $mandys_pink;
            }

            display: flex;
            padding: 20px;
            border-radius: 5px;
            margin: 15px 0 0;
            @include guardsman_red(background, 0.15);
        }

        &__error-icon  {
            font-size: 3.5rem;
            font-family: 'Quicksand-Bold';
            color: $monza;
            margin: 0 18px 0 0;
        }

        &__error-text {
            display: flex;
            flex-direction: column;
        }

        &__error-notification  {
            font-size: 1.8rem;
            font-family: 'Quicksand-Bold';
            color: $black;
        }

        &__error-message {
            font-size: 1.6rem;
            font-family: 'Quicksand-Bold';
            color: $black;
        }
    }
</style>
